import { motion } from 'framer-motion';

const variants = {
  success: 'bg-nexus-accent-mint/20 text-nexus-accent-mint border-nexus-accent-mint/30',
  warning: 'bg-nexus-accent-amber/20 text-nexus-accent-amber border-nexus-accent-amber/30',
  error: 'bg-nexus-error/20 text-nexus-error border-nexus-error/30',
  info: 'bg-nexus-accent-violet/20 text-nexus-accent-violet border-nexus-accent-violet/30',
  premium: 'bg-gradient-to-r from-nexus-accent-amber/20 to-nexus-accent-mint/20 text-nexus-accent-amber border-nexus-accent-amber/30',
  default: 'bg-nexus-bg-tertiary text-nexus-text-secondary border-white/10',
};

export const NexusBadge = ({
  children,
  variant = 'default',
  pulse = false,
  className = '',
  ...rest
}) => {
  return (
    <span
      {...rest}
      className={`
        inline-flex items-center gap-1.5 px-2 py-0.5
        text-xs font-bold tracking-wider uppercase
        border rounded-sm
        ${variants[variant]}
        ${className}
      `}
    >
      {pulse && (
        <motion.span
          animate={{ scale: [1, 1.2, 1], opacity: [1, 0.5, 1] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="w-1.5 h-1.5 rounded-full bg-current"
        />
      )}
      {children}
    </span>
  );
};

export const NexusAvatar = ({
  src,
  name,
  size = 'md',
  status,
  className = '',
}) => {
  const sizes = {
    sm: 'w-8 h-8 text-xs',
    md: 'w-10 h-10 text-sm',
    lg: 'w-12 h-12 text-base',
    xl: 'w-16 h-16 text-lg',
  };
  
  const statusColors = {
    online: 'bg-nexus-accent-mint',
    offline: 'bg-nexus-text-tertiary',
    busy: 'bg-nexus-error',
    away: 'bg-nexus-accent-amber',
  };
  
  const getInitials = (name) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };
  
  // Generate a consistent color based on name
  const getGradient = (name) => {
    const colors = [
      'from-nexus-accent-mint to-nexus-accent-blue',
      'from-nexus-accent-violet to-nexus-accent-mint',
      'from-nexus-accent-amber to-nexus-accent-violet',
      'from-nexus-accent-blue to-nexus-accent-violet',
    ];
    const index = name.charCodeAt(0) % colors.length;
    return colors[index];
  };
  
  return (
    <div className={`relative inline-block ${className}`}>
      <div
        className={`
          ${sizes[size]} rounded-full overflow-hidden
          flex items-center justify-center font-semibold
          ${!src ? `bg-gradient-to-br ${getGradient(name)} text-white` : ''}
        `}
      >
        {src ? (
          <img src={src} alt={name} className="w-full h-full object-cover" />
        ) : (
          getInitials(name)
        )}
      </div>
      
      {status && (
        <span
          className={`
            absolute bottom-0 right-0 w-3 h-3 rounded-full
            border-2 border-nexus-bg-secondary
            ${statusColors[status]}
          `}
        />
      )}
    </div>
  );
};

export const NexusAvatarGroup = ({ avatars, max = 4, size = 'md' }) => {
  const displayed = avatars.slice(0, max);
  const remaining = avatars.length - max;
  
  return (
    <div className="flex -space-x-3">
      {displayed.map((avatar, i) => (
        <NexusAvatar
          key={i}
          src={avatar.src}
          name={avatar.name}
          size={size}
          className="ring-2 ring-nexus-bg-secondary"
        />
      ))}
      {remaining > 0 && (
        <div
          className={`
            w-10 h-10 rounded-full bg-nexus-bg-tertiary
            flex items-center justify-center text-sm font-medium text-nexus-text-secondary
            ring-2 ring-nexus-bg-secondary
          `}
        >
          +{remaining}
        </div>
      )}
    </div>
  );
};
